import React, { useEffect, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { FaSpinner, FaSearch } from 'react-icons/fa';
import AdCard from '../components/AdCard';
import FilterSidebar from '../components/FilterSidebar';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import BackToHome from '../components/BackToHome';
import { validateSearchQuery, sanitizeInput } from '../utils/security';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build filters from query params with sanitization
  const filters = {};
  for (const [key, value] of searchParams.entries()) {
    if (key === 'query') {
      // Validate and sanitize search query
      const searchValidation = validateSearchQuery(value);
      if (!searchValidation.valid) {
        // Invalid search query - show error but don't block (backend will also validate)
        console.warn('Invalid search query:', searchValidation.error);
      }
      filters['search'] = searchValidation.sanitized; // send sanitized value as 'search' to backend
    } else {
      // Sanitize other filter values
      filters[key] = typeof value === 'string' ? sanitizeInput(value) : value;
    }
  }
  const query = searchParams.get('query');

  useEffect(() => {
    const fetchAds = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(API_ENDPOINTS.ADS.GET_ALL, {
          params: filters
        });


        // Backend can return either:
        // 1) Array of ads
        // 2) Object: { ads: [...], matchType, originalQuery, message }
        if (Array.isArray(response.data)) {
          setAds(response.data);
        } else if (response.data && Array.isArray(response.data.ads)) {
          setAds(response.data.ads);

          // If Meilisearch / smart fallback was used, show info toast once
          if (response.data.matchType && response.data.message) {
            // Optional: you could use a toast here if desired
            // toast.info(response.data.message);
          }
        } else {
          console.error('Invalid response format:', response.data);
          setError('Invalid response from server');
        }
      } catch (error) {
        console.error('Search error:', error);
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to load ads. Please try again later.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const handleFilterApply = (newFilters) => {
    // Build query string from filters
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        params.append(key, value);
      }
    });
    navigate(`/search?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <FaSpinner className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div key={location.pathname + location.search} className="content-wrapper">
      <Container fluid>
        <BackToHome />
        <Row>
          {/* Filter Sidebar */}
          <Col md={3}>
            <FilterSidebar onFilterApply={handleFilterApply} />
          </Col>

          {/* Search Results */}
          <Col md={9}>
            <div className="bg-light min-vh-100 py-5">
              <h2 className="text-center mb-5 fw-bold">
                {query 
                  ? `Search Results for "${query}"`
                  : location.state 
                    ? `Listings in ${location.state}`
                    : 'Search Results'}
              </h2>

              {ads.length === 0 ? (
                <div className="text-center py-5">
                  <FaSearch className="text-muted mb-3" size={48} />
                  <h4 className="text-muted">No ads found matching your criteria</h4>
                  <p className="text-muted">Try adjusting your search terms or filters</p>
                </div>
              ) : (
                <>
                  <Alert variant="info" className="mb-4">
                    Found {ads.length} {ads.length === 1 ? 'result' : 'results'} 
                    {query ? ` for "${query}"` : ''}
                    {location.state ? ` in ${location.state}` : ''}
                  </Alert>
                  <Row xs={1} md={2} lg={3} className="g-4">
                    {ads.map((ad) => (
                      <Col key={ad._id}>
                        <AdCard ad={ad} />
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SearchResults; 